import { useEffect, useMemo, useRef } from 'react'

import { useFlagsStatus } from '@unleash/proxy-client-react'

import {
  getKnownMetapicUserId,
  isMetapicUserKnown,
  SessionType,
  useGetSession,
} from '@web-apps/feature-auth'
import { EmbeddedCredentialMode } from '@web-apps/utils-shared'

import {
  useGetUserAccountQuery,
  useLinkMetapicUserMutation,
} from '../../services/user.api'
import { isImpersonating } from '../../utils/helpers/impersonation.helpers'
import { getAccountRestriction } from '../../utils/helpers/metapic.helpers'

export type AuthState =
  | 'loading'
  | 'error'
  | 'anonymous'
  | 'needs-native-auth'
  | 'impersonation-failed'
  | 'needs-metapic-account'
  | 'needs-zezam-account'
  | 'pending-verification'
  | 'suspended'
  | 'ready'

type AccountGate =
  | 'loading'
  | 'error'
  | 'suspended'
  | 'pending-verification'
  | 'ready'

const useZezamAccountGate = ({
  userId,
  session,
}: {
  userId?: string
  session: SessionType | null
}): AccountGate => {
  const {
    data: account,
    isLoading,
    isFetching,
    isError,
  } = useGetUserAccountQuery({ userId: userId ?? '' }, { skip: !userId })

  const [linkMetapicUser, { isLoading: isLinking }] =
    useLinkMetapicUserMutation()
  const hasAttemptedLinkRef = useRef(false)

  const knownMetapicUserId = getKnownMetapicUserId(session)

  const needsMetapicLink = Boolean(
    account &&
      !account.metapicUserId &&
      knownMetapicUserId &&
      !isImpersonating()
  )

  useEffect(() => {
    if (!needsMetapicLink || hasAttemptedLinkRef.current) return
    if (!userId || !knownMetapicUserId) return

    hasAttemptedLinkRef.current = true

    linkMetapicUser({ userId, metapicUserId: knownMetapicUserId })
  }, [needsMetapicLink, userId, knownMetapicUserId, linkMetapicUser])

  if (isLoading) return 'loading'
  if (isError || !account) return 'error'

  if (
    needsMetapicLink &&
    (!hasAttemptedLinkRef.current || isLinking || isFetching)
  )
    return 'loading'

  const restriction = getAccountRestriction({
    suspendedAt: account.suspendedAt,
    verifiedAt: account.verifiedAt,
    isImpersonating: isImpersonating(),
  })

  if (restriction === 'suspended') return 'suspended'
  if (restriction === 'unverified') return 'pending-verification'

  return 'ready'
}

type UseAuthStateParams = {
  isEmbedded: boolean
  embeddedCredentialMode: EmbeddedCredentialMode | null
  impersonatedUserId: string | null
  resolveKey: number
}

export type UseAuthStateResult = {
  state: AuthState
  session: SessionType | null
  userId?: string
  metapicUserId?: number
}

export const useAuthState = ({
  isEmbedded,
  embeddedCredentialMode,
  impersonatedUserId,
  resolveKey,
}: UseAuthStateParams): UseAuthStateResult => {
  const { flagsReady } = useFlagsStatus()

  const { session, status, isSessionLoading } = useGetSession({
    isEmbedded,
    hasEmbeddedCredential: Boolean(embeddedCredentialMode),
    embeddedCredentialMode,
    impersonatedUserId,
    resolveKey,
  })

  const userId = session?.identity?.metadata_public?.zezam_user_id
  const metapicUserId = getKnownMetapicUserId(session)

  const accountGate = useZezamAccountGate({ userId, session })

  const state = useMemo<AuthState>(() => {
    if (!flagsReady || isSessionLoading || status === 'loading')
      return 'loading'

    if (status === 'impersonation-failed') return 'impersonation-failed'

    if (status === 'needs-native-auth') return 'needs-native-auth'

    if (status === 'anonymous')
      return isEmbedded ? 'needs-native-auth' : 'anonymous'

    if (status === 'no-zezam-account') {
      if (impersonatedUserId) return 'impersonation-failed'

      if (isMetapicUserKnown(session) && !metapicUserId)
        return 'needs-metapic-account'

      return 'needs-zezam-account'
    }

    if (!userId) return 'needs-zezam-account'

    if (accountGate === 'loading') return 'loading'
    if (accountGate === 'error') return 'error'

    return accountGate
  }, [
    flagsReady,
    isSessionLoading,
    status,
    isEmbedded,
    impersonatedUserId,
    session,
    metapicUserId,
    userId,
    accountGate,
  ])

  return { state, session, userId, metapicUserId }
}
